export type PriceMode = "gross" | "net";
type PriceModeListener = (priceMode: PriceMode) => void;

const META_SELECTOR = "meta[name='current-pricemode']";
const DEFAULT_PRICE_MODE: PriceMode = "gross";

function parsePriceMode(content: string | null): PriceMode {
    switch (content?.toLowerCase()) {
        case "net":
            return "net";
        case "gross":
            return "gross";
        default:
            return DEFAULT_PRICE_MODE;
    }
}

const listeners = new Set<PriceModeListener>();
let observer: MutationObserver | null = null;

function notify(priceMode: PriceMode): void {
    listeners.forEach((fn) => fn(priceMode));
}

function startObserver(): void {
    const metaElement = document.querySelector<HTMLMetaElement>(META_SELECTOR);

    if (!metaElement) {
        console.debug(`[SSW PriceModeObserver] Meta tag not found: ${META_SELECTOR}.`);
        return;
    }

    observer = new MutationObserver((mutations: MutationRecord[]) => {
        for (const mutation of mutations) {
            if (mutation.type === "attributes" && mutation.attributeName === "content") {
                notify(
                    parsePriceMode((mutation.target as HTMLMetaElement).getAttribute("content"))
                );
                break;
            }
        }
    });

    observer.observe(metaElement, {
        attributes: true,
        attributeFilter: ["content"]
    });
}

function stopObserver(): void {
    observer?.disconnect();
    observer = null;
}

export function getCurrentPriceMode(): PriceMode {
    const meta = document.querySelector<HTMLMetaElement>(META_SELECTOR);
    return parsePriceMode(meta?.getAttribute("content") ?? null);
}

/**
 * A subscription on the `<meta name="current-pricemode" content="value">` tag
 * that provides notifications when the price mode changes.
 */
export function subscribeToPriceMode(listener: PriceModeListener): () => void {
    const isFirst = listeners.size === 0;
    listeners.add(listener);

    if (isFirst) {
        startObserver();
    }

    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            stopObserver();
        }
    };
}
